import { Grid, Stack, Typography, styled } from "@mui/material";
import {
  Row,
  SmText,
  TextButton,
  TextMd,
} from "Components";
import { useEffect, useState, Fragment } from "react";
import { useIntl } from "react-intl";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { useItemContext } from "..";
import { ItemAttributeType } from "types/item";
import { COLORS } from "constants/layout";
import useBreakpoints from "hooks/useBreakpoint";
import { QuotationType } from "types/quotation";

type Props = {
  dataAttr?: QuotationType;
  isNotShowLabel?: boolean;
};

const MAXHEIGHT = 260;
const MAX_ATTR_QLT_DESKTOP = 10;
const MAX_ATTR_QLT_MOBILE = 5;

const TableInformations = (props: Props) => {
  const {dataAttr, isNotShowLabel} = props;
  const intl = useIntl();
  const { data = {} } = useItemContext();
  const { key_attr = [] } = dataAttr? dataAttr : data;

  return (
    <Fragment>
      {key_attr?.length > 0 && (
        <TableInformationsTemplate
          label={isNotShowLabel? "" : intl.formatMessage({ id: "specifications" })}
          datas={key_attr}
        />
      )}
    </Fragment>
  );
};

const TableInformationsTemplate = ({
  label = "",
  datas = [],
}: {
  label: string;
  datas: ItemAttributeType[];
}) => {
  const id = `att_${label}`;
  const intl = useIntl();
  const { isMobile } = useBreakpoints();
  const [showFull, setShowFull] = useState(true);
  const [isOverLimit, setIsOverLimit] = useState(false);

  useEffect(() => {
    if (
      datas.length > (isMobile ? MAX_ATTR_QLT_MOBILE : MAX_ATTR_QLT_DESKTOP)
    ) {
      setIsOverLimit(true);
      setShowFull(false);
    }
  }, [isMobile, datas]);

  const toggleShowMore = () => {
    setShowFull(!showFull);
  };

  return (
    <StyledTableInformations>
      <TextMd component="h2" className="title" mb={2}>
        {label}
      </TextMd>

      <Fragment>
        <Grid
          container
          className={`data_table ${showFull ? "" : "limitshow"}`}
          id={id}
          columnSpacing={2}
        >
          {datas.map((data, i) => {
            const isEvenRow = isMobile
              ? i % 2 === 0
              : Math.floor(i / 2) % 2 === 0;

            return (
              <Grid item xs={12} md={6} key={i}>
                <Row p={2} className={isEvenRow ? "even-row" : ""}>
                  <Typography className="label" variant="body2">
                    {data.key}
                  </Typography>

                  <SmText className="value" component="h3">
                    {data.value}
                  </SmText>
                </Row>
              </Grid>
            );
          })}
        </Grid>

        {isOverLimit && (
          <TextButton
            onClick={toggleShowMore}
            endIcon={showFull ? <IoIosArrowUp /> : <IoIosArrowDown />}
            className="show_button"
          >
            {showFull
              ? intl.formatMessage({ id: "show_less" })
              : intl.formatMessage({ id: "show_more" })}
          </TextButton>
        )}
      </Fragment>
    </StyledTableInformations>
  );
};

const StyledTableInformations = styled(Stack)({
  width: "100%",
  ".data_table": {
    ".label, .value": {
      flex: 1,
    },
    ".label": {
      color: "#667085",
    },
    ".value": {
      fontWeight: 500,
      color: "#667085",
    },
    ".even-row": {
      backgroundColor: "#F2F4F7",
    },
    transition: "max-height 1s ease-in-out",
    maxHeight: "auto",
  },
  ".limitshow": {
    overflow: "hidden",
    maxHeight: MAXHEIGHT,
  },
  ".show_button": {
    width: "fit-content",
    color: COLORS.gray[500],
    marginTop: 8,
    fontSize: 14,
    fontWeight: 400,
    svg: {
      width: 16,
    },
  },
  ".viewall": {
    color: "var(--sub-color)",
    cursor: "pointer",
    fontWeight: 500,
  },
});

export default TableInformations;
